<div class="outdoor-root">

  <!-- ── Full-screen Leaflet map ─────────────────────────────────────────── -->
  <div
    class="map-wrap"
    leaflet
    [leafletOptions]="mapOptions"
    (leafletMapReady)="onMapReady($event)">
  </div>

  <!-- ── Panel toggle button ─────────────────────────────────────────────── -->
  <button class="panel-toggle"
    (click)="togglePanel()"
    [class.panel-open]="panelOpen"
    [class.hidden-mobile-open]="panelOpen"
    [style.left]="panelOpen ? (sidebarWidth + 8) + 'px' : null"
    title="Toggle panel">
    <span>☰</span>
  </button>

  <!-- ── Locate me button ────────────────────────────────────────────────── -->
  <button class="locate-btn"
    [class.hidden-mobile-open]="panelOpen"
    [style.left]="panelOpen ? (sidebarWidth + 8) + 'px' : null"
    (click)="toggleLocation()"
    [class.active]="locationEnabled"
    [title]="locationError ?? (locationEnabled ? 'Stop tracking location' : 'Show my location')">
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <circle cx="12" cy="12" r="3"/>
      <path d="M12 2v3M12 19v3M2 12h3M19 12h3"/>
      <path d="M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8z" stroke="none" fill="currentColor" opacity="0.15"/>
    </svg>
  </button>

  <!-- ── Side / bottom panel ─────────────────────────────────────────────── -->
  <aside class="side-panel" [class.open]="panelOpen" [style.width.px]="sidebarWidth">
    <!-- Resize handle — drag right edge to resize -->
    <div class="resize-handle" (mousedown)="onResizeHandleMousedown($event)"></div>

    <!-- Mode tab bar + clear-all -->
    <div class="mode-tabs">
      <button [class.active]="mode === 'hunting'"  (click)="switchMode('hunting')">Hunting</button>
      <button [class.active]="mode === 'fitness'"  (click)="switchMode('fitness')">Fitness</button>
      <button [class.active]="mode === 'notes'"    (click)="switchMode('notes')">Notes</button>
      <button [class.active]="mode === 'photos'"   (click)="switchMode('photos')">Photos</button>
      <button [class.active]="mode === 'coverage'" (click)="switchMode('coverage')">Coverage</button>
      <button [class.active]="mode === 'jagd'"     (click)="switchMode('jagd')">Jagd</button>
      <button class="clear-all-btn" (click)="clearAll()" title="Remove everything from map">✕ all</button>
      <!-- Mobile-only: locate + close panel buttons -->
      <button class="tab-icon-btn mobile-only" (click)="toggleLocation()" [class.active]="locationEnabled" title="My location">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="3"/><path d="M12 2v3M12 19v3M2 12h3M19 12h3"/>
        </svg>
      </button>
      <button class="tab-icon-btn mobile-only" (click)="togglePanel()" title="Close panel">✕</button>
    </div>

    <div class="panel-body">

      <!-- ── HUNTING ──────────────────────────────────────────────────────── -->
      @if (mode === 'hunting') {
        @if (loadingSessions) {
          <div class="loading">Loading sessions…</div>
        } @else if (sessions.length === 0) {
          <div class="empty">No sessions found.</div>
        } @else {
          <ul class="item-list">
            @for (session of sessions; track session.id) {
              <li
                class="item"
                [class.active]="shownSessionIds.has(session.id)"
                (click)="loadSession(session)">
                <div class="item-title">{{ session.label }}</div>
                <div class="item-meta">
                  @if (session.hasDogTrack) {
                    <span class="badge dog">Dog</span>
                  }
                  @if (session.hasHandheldTrack) {
                    <span class="badge handheld">Handheld</span>
                  }
                  @if (shownSessionIds.has(session.id)) {
                    <span class="badge on-map">on map</span>
                  }
                </div>
                @if (loadingSessionId === session.id) {
                  <div class="item-loading">Loading…</div>
                }
              </li>
            }
          </ul>
        }
      }

      <!-- ── FITNESS ──────────────────────────────────────────────────────── -->
      @if (mode === 'fitness') {
        @if (loadingFit) {
          <div class="loading">Loading activities…</div>
        } @else if (fitActivities.length === 0) {
          <div class="empty">No FIT activities found.</div>
        } @else {
          <div class="list-toolbar">
            <button class="btn-ghost small" (click)="enableAllFit()">Enable all</button>
            <button class="btn-ghost small" (click)="disableAllFit()">Disable all</button>
          </div>
          <ul class="item-list">
            @for (act of fitActivities; track act.id) {
              <li
                class="item"
                [class.active]="shownFitIds.has(act.id)"
                (click)="loadFitActivity(act)">
                <div class="item-title">{{ act.name }}</div>
                <div class="item-meta">
                  <span>{{ formatDist(act.distanceKm) }}</span>
                  <span>{{ formatDuration(act.durationMinutes) }}</span>
                  <span class="point-count">{{ act.pointCount }} pts</span>
                  @if (shownFitIds.has(act.id)) {
                    <span class="badge on-map">on map</span>
                  }
                </div>
              </li>
            }
          </ul>
        }
      }

      <!-- ── NOTES ────────────────────────────────────────────────────────── -->
      @if (mode === 'notes') {
        @if (loadingNotes) {
          <div class="loading">Loading notes…</div>
        } @else {
          <div class="notes-toolbar">
            <button
              class="btn-ghost"
              [class.btn-active]="notesVisible"
              (click)="toggleNotesVisible()">
              {{ notesVisible ? 'Hide from map' : 'Show on map' }}
            </button>
            @if (!addingNote && editingNote === null) {
              <button class="btn-primary" (click)="startAddNote()">+ Place on map</button>
              @if (hasGeolocation) {
                <button class="btn-primary" (click)="placeNoteHere()" title="Use current GPS position">+ Here</button>
              }
            }
            @if (addingNote) {
              <div class="hint">Click on the map…
                <button class="btn-ghost" (click)="cancelEdit()">Cancel</button>
              </div>
            }
          </div>

          @if (editingNote !== null) {
            <div class="note-editor">
              <input
                class="note-input"
                type="text"
                placeholder="Title"
                [(ngModel)]="editingNote!.title"
                autofocus />
              <textarea
                class="note-input"
                placeholder="Notes (optional)"
                rows="3"
                [(ngModel)]="editingNote!.body">
              </textarea>
              <div class="note-editor-actions">
                <button class="btn-primary" (click)="saveNote()">Save</button>
                <button class="btn-ghost"   (click)="cancelEdit()">Cancel</button>
              </div>
            </div>
          }

          @if (notes.length === 0 && notesLoaded && !addingNote && editingNote === null) {
            <div class="empty">No notes yet.</div>
          } @else if (notesLoaded) {
            <ul class="item-list">
              @for (note of notes; track note.id) {
                <li class="item note-item" (dblclick)="jumpToNote(note)">
                  <div class="item-title">{{ note.title }}</div>
                  @if (note.body) {
                    <div class="note-body">{{ note.body }}</div>
                  }
                  @if (note.createdAt) {
                    <div class="note-date">{{ note.createdAt | date:'yyyy-MM-dd HH:mm' }}</div>
                  }
                  <div class="note-actions">
                    <button class="btn-ghost small" (click)="editNote(note)">Edit</button>
                    <button class="btn-ghost small danger" (click)="deleteNote(note)">Delete</button>
                  </div>
                </li>
              }
            </ul>
          }
        }
      }

      <!-- ── PHOTOS ───────────────────────────────────────────────────────── -->
      @if (mode === 'photos') {
        @if (loadingPhotos) {
          <div class="loading">Loading photos…</div>
        } @else {
          <div class="photo-info">
            <button
              class="btn-ghost"
              [class.btn-active]="photosVisible"
              (click)="togglePhotosVisible()">
              {{ photosVisible ? 'Hide photos from map' : 'Show photos on map' }}
            </button>
            @if (photosVisible && photos.length > 0) {
              @if (editingPhotoPosition) {
                <div class="hint" style="margin-top:10px">Click on the map to set the new position…
                  <button class="btn-ghost" (click)="cancelEdit()">Cancel</button>
                </div>
              } @else {
                <div class="photo-count" style="margin-top:10px">{{ photos.length }} geo-tagged photos on map</div>
                <p class="photo-hint">Click a marker on the map to view.</p>
              }
            }
          </div>
        }
      }

      <!-- ── COVERAGE ──────────────────────────────────────────────────────── -->
      @if (mode === 'coverage') {
        @if (loadingCoverage) {
          <div class="loading">Computing coverage…</div>
        } @else if (coverageLoaded) {
          <div class="coverage-info">
            <div class="coverage-stat">{{ coverageTrackCount }} activities processed</div>
            <p class="coverage-hint">
              Orange overlay shows areas you have visited. Intensity reflects how
              recently and how often — fading with a {{ coverageHalfLife }}-month half-life.
            </p>
            <button class="btn-ghost coverage-toggle" (click)="toggleCoverageVisibility()">
              {{ coverageVisible ? 'Hide overlay' : 'Show overlay' }}
            </button>

            <div class="coverage-settings">

              <div class="setting-row">
                <div class="setting-label"><span>Color palette</span></div>
                <select class="setting-select" [value]="coveragePalette" (change)="onCoveragePaletteChange($event)">
                  <option value="plasma">Plasma (purple → orange → yellow)</option>
                  <option value="fire">Fire (dark red → orange → yellow)</option>
                  <option value="magma">Magma (black → purple → red → pale)</option>
                  <option value="purplered">Purple–Red (no green)</option>
                  <option value="weather">Weather rainbow</option>
                </select>
              </div>

              <div class="setting-row">
                <div class="setting-label">
                  <span>Contrast</span>
                  <span class="setting-value">{{ coverageContrast.toFixed(1) }}</span>
                </div>
                <input type="range" min="0.2" max="2.0" step="0.1"
                       [value]="coverageContrast"
                       (input)="onCoverageRenderChange($event, 'contrast')">
                <div class="setting-hints"><span>flat</span><span>steep</span></div>
              </div>

              <div class="setting-row">
                <div class="setting-label">
                  <span>Rare visit brightness</span>
                  <span class="setting-value">{{ (coverageMinBrightness * 100).toFixed(0) }}%</span>
                </div>
                <input type="range" min="0" max="0.5" step="0.01"
                       [value]="coverageMinBrightness"
                       (input)="onCoverageRenderChange($event, 'minBrightness')">
                <div class="setting-hints"><span>invisible</span><span>bright</span></div>
              </div>

              <div class="setting-row">
                <div class="setting-label">
                  <span>Decay half-life</span>
                  <span class="setting-value">{{ coverageHalfLife }} mo</span>
                </div>
                <input type="range" min="1" max="60" step="1"
                       [value]="coverageHalfLife"
                       (change)="onCoverageRecomputeChange($event, 'halfLife')">
                <div class="setting-hints"><span>1 mo</span><span>5 yr</span></div>
              </div>

              <div class="setting-row">
                <div class="setting-label">
                  <span>Kernel radius</span>
                  <span class="setting-value">{{ coverageKernelRadius }} m</span>
                </div>
                <input type="range" min="20" max="300" step="10"
                       [value]="coverageKernelRadius"
                       (change)="onCoverageRecomputeChange($event, 'kernelRadius')">
                <div class="setting-hints"><span>20 m</span><span>300 m</span></div>
              </div>

            </div>
          </div>
        } @else {
          <div class="empty">Switch to this tab to load coverage data.</div>
        }
      }

      <!-- ── JAGDGEBIETE ──────────────────────────────────────────────────── -->
      @if (mode === 'jagd') {
        @if (loadingJagd) {
          <div class="loading">Loading Jagdgebiete…</div>
        } @else if (jagdgebiete.length === 0 && jagdLoaded) {
          <div class="empty">No Jagdgebiete found.</div>
        } @else if (jagdLoaded) {
          <div class="list-toolbar">
            <button class="btn-ghost small" (click)="findJagdgebiet()">🔍 Find</button>
            <button class="btn-ghost small" (click)="showAllJagd()">Show all</button>
            <button class="btn-ghost small" (click)="hideAllJagd()">Hide all</button>
          </div>
          <ul class="item-list">
            @for (area of jagdgebiete; track area.id) {
              <li class="item jagd-item"
                  [class.active]="shownJagdIds.has(area.id)"
                  (click)="toggleJagdgebiet(area)"
                  (dblclick)="jumpToJagdgebiet(area)">
                <div class="item-title">{{ area.revierName ?? ('Revier ' + area.revierId) }}</div>
                <div class="item-meta">
                  @if (area.typ) {
                    <span class="badge" [class.jagd-eigen]="area.typ.toLowerCase().includes('eigen')"
                                        [class.jagd-gemeinde]="!area.typ.toLowerCase().includes('eigen')">
                      {{ area.typ }}
                    </span>
                  }
                  @if (area.flaecheHa != null) {
                    <span class="jagd-ha">{{ formatHa(area.flaecheHa) }}</span>
                  }
                  @if (shownJagdIds.has(area.id)) {
                    <span class="badge on-map">on map</span>
                  }
                </div>
                @if (area.hegerName) {
                  <div class="jagd-detail">Heger: {{ area.hegerName }}</div>
                }
                @if (area.bescheid) {
                  <div class="jagd-detail">Bescheid: {{ area.bescheid }}</div>
                }
                @if (area.rechtsstand || area.stand) {
                  <div class="jagd-detail">
                    @if (area.rechtsstand) { Rechtsstand: {{ area.rechtsstand }} }
                    @if (area.rechtsstand && area.stand) { · }
                    @if (area.stand) { Stand: {{ area.stand }} }
                  </div>
                }
              </li>
            }
          </ul>
        }
      }

  <!-- ── Bottom bar: trim + sync (hunting mode, both tracks loaded) ─────── -->
  @if (timelineVisible) {
    <div class="timeline-bar">

      <!-- Row 1: trim range — cut noisy start/end -->
      <div class="tl-row">
        <span class="tl-label tl-time">
          <span>{{ trimStartDate | date:'yyyy-MM-dd' }}</span>
          <span>{{ trimStartDate | date:'HH:mm' }}</span>
        </span>
        <div class="trim-range-wrap">
          <div class="trim-track"></div>
          <div class="trim-fill"
               [style.left.%]="trimFillLeft"
               [style.width.%]="trimFillWidth"></div>
          <input class="trim-input" type="range" min="0" max="1000"
                 [value]="trimStart" (input)="onTrimStartInput($event)">
          <input class="trim-input" type="range" min="0" max="1000"
                 [value]="trimEnd" (input)="onTrimEndInput($event)">
        </div>
        <span class="tl-label tl-time" style="text-align:right">
          <span>{{ trimEndDate | date:'yyyy-MM-dd' }}</span>
          <span>{{ trimEndDate | date:'HH:mm' }}</span>
        </span>
      </div>

      <!-- Row 2: sync position scrubber -->
      <div class="tl-row">
        <span class="tl-label tl-time">
          <span>{{ currentSyncDate | date:'yyyy-MM-dd' }}</span>
          <span>{{ currentSyncDate | date:'HH:mm:ss' }}</span>
        </span>
        <input
          class="tl-slider"
          type="range"
          [min]="trimStart"
          [max]="trimEnd"
          [value]="timelineCurrent"
          (input)="onTimelineInput($event)" />
        <span class="tl-label tl-label-right">
          {{ activeSession?.label }}
        </span>
      </div>

    </div>
  }

  <!-- ── Bottom bar: position scrubber (fitness mode, track loaded) ───────── -->
  @if (fitTimelineVisible) {
    <div class="timeline-bar">
      <div class="tl-row">
        <span class="tl-label tl-time">
          <span>{{ fitCurrentDate | date:'yyyy-MM-dd' }}</span>
          <span>{{ fitCurrentDate | date:'HH:mm:ss' }}</span>
        </span>
        <input
          class="tl-slider"
          type="range"
          min="0" max="1000"
          [value]="fitTimelineCurrent"
          (input)="onFitTimelineInput($event)" />
        <span class="tl-label tl-label-right">{{ activeFitActivity?.name }}</span>
      </div>
    </div>
  }

</div>

<!-- ── Full-size photo lightbox (outside side-panel so inset:0 = full screen) -->
@if (viewingPhoto) {
  <div class="photo-lightbox" (click)="closePhotoViewer()">
    <div class="photo-lightbox-content" (click)="$event.stopPropagation()">
      <button class="photo-lightbox-close" (click)="closePhotoViewer()" title="Close">✕</button>
      @if (viewingPhotoLoading) {
        <div class="photo-lightbox-spinner">Loading…</div>
      } @else if (viewingPhotoBlobUrl) {
        <img class="photo-lightbox-img" [src]="viewingPhotoBlobUrl" [alt]="viewingPhoto.title ?? 'Photo'">
      }
      <div class="photo-lightbox-meta">
        @if (viewingPhoto.title) { <span class="photo-lightbox-title">{{ viewingPhoto.title }}</span> }
        <span class="photo-lightbox-date">{{ viewingPhoto.takenAt | date:'yyyy-MM-dd HH:mm:ss':'Europe/Vienna' }}</span>
      </div>
    </div>
  </div>
}
